import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Mail, Menu, X, User, LogOut } from 'lucide-react';
import { businessInfo } from '../mock';
import { Button } from './ui/button';
import LanguageToggle from './LanguageToggle';
import { useLanguage } from '../LanguageContext';

const Header = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [customer, setCustomer] = useState(() => {
    const storedCustomer = sessionStorage.getItem('customer');
    return storedCustomer ? JSON.parse(storedCustomer) : null;
  });

  // Listen for storage changes (for logout from other components)
  useEffect(() => {
    const handleStorageChange = () => {
      const storedCustomer = sessionStorage.getItem('customer');
      setCustomer(storedCustomer ? JSON.parse(storedCustomer) : null);
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const handleLogout = () => {
    sessionStorage.removeItem('customer');
    sessionStorage.removeItem('customerToken');
    setCustomer(null);
    setMobileMenuOpen(false);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm">
      {/* Top bar - simplified for mobile */}
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-1.5 sm:py-2">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center text-xs sm:text-sm">
            <a href={`tel:${businessInfo.phone}`} className="flex items-center gap-1.5 hover:text-teal-100 transition-colors">
              <Phone size={14} />
              <span>{businessInfo.phone}</span>
            </a>
            <div className="hidden sm:flex items-center gap-1.5">
              <Mail size={14} />
              <span className="hidden md:inline">{businessInfo.email}</span>
            </div>
            <div className="text-xs font-medium truncate max-w-[140px] sm:max-w-none">
              {businessInfo.laundromat_hours}
            </div>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <nav className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14 sm:h-16">
          {/* Logo/Brand - compact on mobile */}
          <div 
            className="flex-shrink-0 cursor-pointer"
            onClick={() => navigate('/')}
          >
            <h1 className="text-base sm:text-xl lg:text-2xl font-bold text-teal-600 leading-tight">
              <span className="hidden sm:inline">{businessInfo.name}</span>
              <span className="sm:hidden">Laundry 24hr</span>
            </h1>
          </div>

          {/* Desktop navigation */}
          <div className="hidden lg:flex items-center gap-4 xl:gap-6">
            <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-teal-600 transition-colors font-medium text-sm">
              {t('nav.services')}
            </button>
            <button onClick={() => scrollToSection('how-it-works')} className="text-gray-700 hover:text-teal-600 transition-colors font-medium text-sm">
              {t('nav.howItWorks')}
            </button>
            <button onClick={() => scrollToSection('service-areas')} className="text-gray-700 hover:text-teal-600 transition-colors font-medium text-sm">
              {t('nav.areas')}
            </button>
            <button onClick={() => scrollToSection('testimonials')} className="text-gray-700 hover:text-teal-600 transition-colors font-medium text-sm">
              {t('nav.testimonials')}
            </button>
            
            {/* Language Toggle */}
            <LanguageToggle />
            
            {/* Account/Login Button */}
            {customer ? (
              <Button 
                variant="outline" 
                onClick={() => navigate('/account')} 
                className="border-teal-600 text-teal-600 hover:bg-teal-50 text-sm px-3"
              >
                <User size={16} className="mr-1" />
                {customer.name.split(' ')[0]}
              </Button>
            ) : (
              <Button 
                variant="outline" 
                onClick={() => navigate('/login')} 
                className="border-teal-600 text-teal-600 hover:bg-teal-50 text-sm px-3"
              >
                <User size={16} className="mr-1" />
                {t('nav.login')}
              </Button>
            )}
            
            <Button onClick={() => scrollToSection('book-now')} className="bg-teal-600 hover:bg-teal-700 text-white text-sm px-4">
              Book Your Free Pickup
            </Button>
          </div>

          {/* Mobile: Login + Book Now + Menu */}
          <div className="flex lg:hidden items-center gap-2">
            {customer ? (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/account')} 
                className="border-teal-600 text-teal-600 hover:bg-teal-50 px-2 h-8 text-xs"
              >
                <User size={14} />
              </Button>
            ) : (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/login')} 
                className="border-teal-600 text-teal-600 hover:bg-teal-50 px-2 h-8 text-xs"
              >
                <User size={14} />
              </Button>
            )}
            
            <Button 
              size="sm"
              onClick={() => scrollToSection('book-now')} 
              className="bg-teal-600 hover:bg-teal-700 text-white px-2 sm:px-3 h-8 text-xs"
            >
              Book
            </Button>
            
            {/* Hamburger menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile navigation menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t bg-white">
            <div className="flex flex-col gap-3">
              <button onClick={() => scrollToSection('services')} className="text-left text-gray-700 hover:text-teal-600 transition-colors font-medium py-2">
                {t('nav.services')}
              </button>
              <button onClick={() => scrollToSection('how-it-works')} className="text-left text-gray-700 hover:text-teal-600 transition-colors font-medium py-2">
                {t('nav.howItWorks')}
              </button>
              <button onClick={() => scrollToSection('service-areas')} className="text-left text-gray-700 hover:text-teal-600 transition-colors font-medium py-2">
                {t('nav.areas')}
              </button>
              <button onClick={() => scrollToSection('testimonials')} className="text-left text-gray-700 hover:text-teal-600 transition-colors font-medium py-2">
                {t('nav.testimonials')}
              </button>
              
              {/* Language Toggle in Mobile */}
              <div className="py-2 border-t border-b">
                <LanguageToggle />
              </div>
              
              {/* Mobile Account/Login */}
              {customer ? (
                <>
                  <button 
                    onClick={() => { navigate('/account'); setMobileMenuOpen(false); }}
                    className="text-left text-gray-700 hover:text-teal-600 transition-colors font-medium flex items-center gap-2 py-2"
                  >
                    <User size={18} />
                    {t('nav.account')}
                  </button>
                  <button 
                    onClick={handleLogout}
                    className="text-left text-red-600 hover:text-red-700 transition-colors font-medium flex items-center gap-2 py-2"
                  >
                    <LogOut size={18} />
                    Logout
                  </button>
                </>
              ) : (
                <button 
                  onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                  className="text-left text-gray-700 hover:text-teal-600 transition-colors font-medium flex items-center gap-2 py-2"
                >
                  <User size={18} />
                  {t('nav.login')} / Sign Up
                </button>
              )}
              
              <Button onClick={() => scrollToSection('book-now')} className="bg-teal-600 hover:bg-teal-700 text-white w-full mt-2">
                Book Your Free Pickup
              </Button>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
